/**
 * A component for collecting, uploading, and managing document files.
 * Supports multiple document types with file upload and deletion functionality.
 */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button, FileIcon, Flex, Icon, Text } from "@jsq/ds/core";
import { useFormContext, Form, FormCheckbox } from "@jsq/ds/forms";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import useUnsavedChangesTracker from "../../../hooks/documents/useUnsavedChangesTracker";
import { ButtonWrapper } from "../../common/ButtonWrapper";
import { DefaultModal } from "../../common/DefaultModal";
import { SingleFileUpload } from "../../common/SingleFileUpload";
import {
  deleteIcon,
  deleteIconContainer,
  uploaderInfoContainer,
  uploaderInfoIcon,
  uploaderInfoText,
} from "./style_vanilla.css";
import {
  DocumentCollectorProps,
  DocumentFormProps,
  DocumentData,
  DocumentInvitedUserInfo,
} from "./types";
import { prepareFilesToUpload } from "./utils";

// Supported file formats that can be uploaded through the document collector
const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

/**
 * Renders the form fields for document collection with file upload controls
 * for each document type specified.
 *
 * @param documentDetails - Array of document specifications to collect
 * @param isSubmitting - Whether the form is currently submitting
 * @param onNavigateBack - Optional callback for navigation to previous screen
 */
const DocumentsForm: React.FC<{
  documentDetails: DocumentFormProps[];
  isSubmitting: boolean;
  submitFormButton?: string;
  showNoDocumentsTT?: boolean;
  allowSkipToUpload?: boolean;
  onNavigateBack?: () => void;
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
  initialFiles: DocumentData[];
}> = ({
  documentDetails,
  isSubmitting,
  submitFormButton,
  showNoDocumentsTT,
  allowSkipToUpload,
  onNavigateBack,
  onUnsavedChangesChange,
  initialFiles,
}) => {
  const [documentOwner, setDocumentOwner] = useState<DocumentInvitedUserInfo | null>();
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [deletedIdsOfGuest, setDeletedIdsOfGuest] = useState<number[]>([]);
  const [hasAttachedDocuments, setHasAttachedDocuments] = useState(false);
  const formContext = useFormContext<DocumentFormValues>();

  const showGuestInfo = (data: DocumentFormProps) => {
    if (data.uploaderInfo && data.id && !deletedIdsOfGuest.includes(data.id)) {
      return true;
    }
    return false;
  };

  const disableSubmit = useMemo(() => {
    if (formContext.values.skipDocumentUpload) {
      return false;
    }

    return showNoDocumentsTT && !enableSubmit;
  }, [showNoDocumentsTT, enableSubmit, formContext.values.skipDocumentUpload]);

  useEffect(() => {
    const hasNewFiles = formContext.values.documents.some((document) => {
      return document.file != null;
    });
    setEnableSubmit(hasNewFiles);
    setHasAttachedDocuments(hasNewFiles);
  }, [formContext.values, documentDetails, formContext]);

  // Track unsaved changes and notify parent component
  useUnsavedChangesTracker(initialFiles, deletedIdsOfGuest, onUnsavedChangesChange);
  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      {allowSkipToUpload && (
        <Flex justifyContent="flex-start">
          <FormCheckbox
            name="skipDocumentUpload"
            label="I cannot provide any of the documents listed below"
            disabled={hasAttachedDocuments}
          />
        </Flex>
      )}
      {documentDetails.map((data, index) => (
        <Flex key={index} flexDirection="column">
          <SingleFileUpload
            title={data.title}
            description={data.description}
            initialFile={data.file}
            acceptedFileFormats={SUPPORTED_FORMATS}
            isDisabled={showGuestInfo(data) || formContext.values.skipDocumentUpload}
            onFileSelected={(file) => {
              void formContext.setFieldValue(`documents[${index}].file`, file);
              return;
            }}
            onFileDeleted={() => {
              void formContext.setFieldValue(`documents[${index}].file`, null);
              return;
            }}
          />
          {showGuestInfo(data) && (
            <Flex className={uploaderInfoContainer}>
              <Flex className={uploaderInfoIcon}>
                <FileIcon extension="" size="x-large" />
              </Flex>
              <Text variant="regular-md" className={uploaderInfoText}>
                Uploaded by {data.uploaderInfo}
              </Text>
              <Flex className={deleteIconContainer}>
                <Icon
                  data-testid={`delete-icon-${data.id}`}
                  type="trash"
                  size="large"
                  className={deleteIcon}
                  color={Colors.Neutral900}
                  onClick={() => {
                    setDocumentOwner({
                      id: data.id!,
                      emailAddress: data.uploaderInfo!,
                    });
                  }}
                />
              </Flex>
              <DefaultModal
                show={!!documentOwner}
                modalSize="small"
                title="Warning"
                body={`Are you sure you want to delete this document? You will not be able to use this document without submitting a new request to ${documentOwner?.emailAddress}`}
                negBtnText="Cancel"
                posBtnText="Delete"
                posBtnVariant="danger"
                onClose={() => {
                  setDocumentOwner(null);
                }}
                onConfirm={() => {
                  void formContext.setFieldValue(`documents[${index}].file`, null);
                  setDeletedIdsOfGuest([...deletedIdsOfGuest, documentOwner!.id]);
                  setDocumentOwner(null);
                }}
              />
            </Flex>
          )}
        </Flex>
      ))}
      <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
        {onNavigateBack && (
          <Button variant="neutral" onClick={onNavigateBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        <ButtonWrapper
          tooltip={"At least one document must be uploaded in order to submit"}
          disabled={disableSubmit}
        >
          <Button type="submit" isLoading={isSubmitting} disabled={disableSubmit}>
            {submitFormButton || "Complete"}
          </Button>
        </ButtonWrapper>
      </Flex>
    </Flex>
  );
};

/**
 * Main component for collecting document files with support for uploading, replacing,
 * and deleting documents. Tracks which files are new, which are replacements, and
 * which have been removed.
 *
 * @param documentDetails - Array of document details to collect
 * @param onNavigateBack - Optional callback for handling navigation to previous screen
 * @param onComplete - Callback for handling form submission with files to delete and upload
 */
export const DocumentCollector: React.FC<DocumentCollectorProps> = ({
  documentDetails,
  isSubmitting,
  buttonConfig,
  allowSkipToUpload = false,
  onNavigateBack,
  onComplete,
  onUnsavedChangesChange,
}) => {
  const [documentFormValues, setDocumentFormValues] = useState<
    DocumentFormValues | undefined
  >();
  const [modalType, setModalType] = useState<"missing-docs" | "no-docs-available">(
    "missing-docs",
  );

  // Convert DocumentFormProps to DocumentData (extract only essential properties)
  const initialFiles = useMemo(() => {
    return documentDetails.map((detail) => ({
      id: detail.id,
      file: detail.file,
      baseType: detail.baseType,
      subType: detail.subType,
      uploaderInfo: detail.uploaderInfo,
    }));
  }, [documentDetails]);

  const uploadHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const { newFiles, deleteDocIds } = prepareFilesToUpload(newSelectedFiles);
      onComplete(newFiles, deleteDocIds);
    },
    [onComplete],
  );

  const submitHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      if (newSelectedFiles.skipDocumentUpload) {
        // User explicitly said documents are not available - show confirmation modal
        setModalType("no-docs-available");
        setDocumentFormValues(newSelectedFiles);
        return;
      }

      const hasDocumentsNotAvailable = newSelectedFiles.documents.some(
        (document: DocumentData) => document.file == null,
      );
      if (hasDocumentsNotAvailable) {
        // Some documents are missing - show missing documents modal
        setModalType("missing-docs");
        setDocumentFormValues(newSelectedFiles);
      } else {
        uploadHandler(newSelectedFiles);
      }
    },
    [uploadHandler],
  );

  return (
    <div>
      <Form<DocumentFormValues>
        initialValues={{
          documents: documentDetails.map((input) => ({
            id: input.id,
            file: input.file,
            baseType: input.baseType,
            subType: input.subType,
            uploaderInfo: input.uploaderInfo,
          })),
          skipDocumentUpload: false,
        }}
        submitOnlyChangedValues={false}
        onSubmit={submitHandler}
      >
        <DocumentsForm
          documentDetails={documentDetails}
          isSubmitting={isSubmitting}
          submitFormButton={buttonConfig?.label}
          showNoDocumentsTT={buttonConfig?.showNoDocumentsTT}
          allowSkipToUpload={allowSkipToUpload}
          onNavigateBack={onNavigateBack}
          onUnsavedChangesChange={onUnsavedChangesChange}
          initialFiles={initialFiles}
        />
      </Form>

      <DefaultModal
        show={!!documentFormValues}
        modalSize="medium"
        title={modalType === "no-docs-available" ? "Warning" : "Missing document warning"}
        body={
          modalType === "no-docs-available"
            ? "You have indicated that you cannot provide any of the requested documents. Are you sure you want to submit without uploading any documents?"
            : "You can continue and sign the subscription, but the subscription cannot be accepted until all required docs are uploaded."
        }
        negBtnText="Cancel"
        posBtnText={modalType === "no-docs-available" ? "Submit" : "Continue"}
        onClose={() => {
          setDocumentFormValues(undefined);
        }}
        onConfirm={() => {
          uploadHandler(documentFormValues!);
          setDocumentFormValues(undefined);
        }}
      />
    </div>
  );
};
